<file-picker-modal>
    <dialog class="modal modal-middle" ref="dialog">
        <div class="modal-box max-w-4xl max-h-[90vh] overflow-y-auto">
            <form method="dialog">
                <button class="btn btn-sm btn-circle btn-ghost absolute end-2 top-2" aria-label="{locale?.t('close')}">✕</button>
            </form>

            <h3 class="font-bold text-lg mb-2">
                {state.repositoryName} - {locale?.t('admin_filepicker_browsefiles')}
            </h3>
            <p class="text-sm text-gray-500 mb-4">
                {isSingleSelect() ? locale?.t('admin_filepicker_selectfile') : locale?.t('admin_filepicker_selectfiles')}
            </p>

            <div if="{state.showFilePicker && !state.lockedCollection && !state.hideCollection}" class="flex flex-wrap items-center gap-3 mb-6">
                <label id="filepicker-collection-label" class="label-text font-semibold text-primary whitespace-nowrap">{locale?.t('collection')}:</label>
                <div class="w-full max-w-xs">
                    <single-select
                        list="{state.collections}"
                        idproperty="collectionId"
                        displayproperty="displayName"
                        selected="{state.selectedCollectionId}"
                        loading="{!state.collectionsLoaded}"
                        labelledby="filepicker-collection-label"
                        fitcontent="{false}"
                        onupdated="{onCollectionSelected}">
                    </single-select>
                </div>
            </div>

            <!-- File Picker Component -->
            <file-picker
                if="{state.showFilePicker && state.repositoryId}"
                key="{state.modalKey}"
                repositoryId="{state.repositoryId}"
                provider="{state.provider}"
                maxfiles="{state.maxFiles}"
                onFilesSelected="{handleFilesSelected}">
            </file-picker>

            <div class="modal-action">
                <button class="btn" onclick="{close}">{locale?.t('cancel')}</button>
            </div>

            <!-- Help Text (collapsible). Hidden in single-select/reupload mode:
                 it describes the multi-file admin import workflow (queueing,
                 Pending Files Review) which does not apply to a single-file
                 reupload that reingests directly into an existing resource. -->
            <div if="{state.showFilePicker && !isSingleSelect()}" class="collapse filepicker-help bg-info text-info-content mt-4">
                <input type="checkbox" aria-label="{locale?.t('admin_filepicker_whatdoes')}" />
                <div class="collapse-title text-sm font-semibold flex items-center gap-3 pe-4">
                    <i class="icon-info1 text-lg shrink-0"></i>
                    <span class="flex-1">{locale?.t('admin_filepicker_whatdoes')}</span>
                    <i class="filepicker-help-chevron icon-arrow_forward_ios shrink-0 relative -top-px"></i>
                </div>
                <div class="collapse-content text-xs">
                    <ul class="list-disc list-inside space-y-1">
                        <raw html="{locale?.t('admin_filepicker_help_list')}"></raw>
                    </ul>
                </div>
            </div>
        </div>
        <form method="dialog" class="modal-backdrop">
            <button>close</button>
        </form>
    </dialog>

    <script>
        import filePicker from '../integrations/file-picker.tag';
        import singleSelect from '../../single-select.tag';
        import raw from '../../raw.tag';
        import { getCollectionService } from '../../../services/service-loader';

        export default {
            get components() {
                return {
                    filePicker,
                    'single-select': singleSelect,
                    raw
                };
            },

            state: {
                repositoryId: null,
                provider: null,
                repositoryName: '',
                onFilesSelectedCallback: null,
                modalKey: 0,
                showFilePicker: false,
                collections: [],
                collectionsLoaded: false,
                selectedCollectionId: '',
                lockedCollection: false,
                maxFiles: 0,
                hideCollection: false,
                onCancelCallback: null
            },

            // A single-file selection (maxFiles === 1, the reupload flow) drives
            // the singular subtitle and hides the multi-file help panel.
            isSingleSelect() {
                return Number(this.state.maxFiles) === 1;
            },

            // options.presetCollectionId pins a collection; with
            // options.lockCollection the selector is hidden so the import is
            // fixed to that collection (Upload to Collection modal).
            // options.maxFiles caps how many files can be selected (1 = single
            // select, for the reupload modal); 0/unset means unlimited.
            // options.hideCollection removes the collection selector entirely
            // and drops the collection mention from the subtitle (reupload,
            // which reingests into an existing resource - no collection).
            // options.onCancel fires when the picker is dismissed without a
            // selection, so a host that stepped aside can restore itself.
            show(repositoryId, provider, repositoryName, onFilesSelected, options = {}) {
                const { presetCollectionId = '', lockCollection = false, maxFiles = 0, hideCollection = false, onCancel = null } = options || {};
                // A fresh open hasn't confirmed a selection yet; onModalClosed
                // uses this to tell a cancel from a successful pick.
                this._filesConfirmed = false;
                // The route this picker belongs to. It's mounted app-level
                // (index.tag), so a route change swaps the page underneath it
                // without closing it - closeForNavigation compares against this.
                this._openedAtHash = window.location.hash;
                // First set showFilePicker to false to unmount any existing instance
                this.update({
                    showFilePicker: false
                });

                // Then set new data and remount with new key
                setTimeout(() => {
                    this.update({
                        repositoryId,
                        provider,
                        repositoryName: repositoryName || this.locale?.t('filepicker_repository'),
                        onFilesSelectedCallback: onFilesSelected,
                        modalKey: this.state.modalKey + 1,
                        showFilePicker: true, // Now mount the file-picker
                        collections: [],
                        collectionsLoaded: false,
                        selectedCollectionId: presetCollectionId || '',
                        lockedCollection: lockCollection && !!presetCollectionId,
                        maxFiles: Number(maxFiles) || 0,
                        hideCollection: !!hideCollection,
                        onCancelCallback: onCancel
                    });
                    this.$('dialog').showModal();
                    this._dialogWasOpen = true;
                    // Reupload hides the collection selector, so there's nothing
                    // to populate - skip the collections fetch entirely.
                    if (!hideCollection) {
                        this.loadCollections();
                    }
                }, 0);
            },

            async loadCollections() {
                try {
                    const collectionService = await getCollectionService();
                    const result = await collectionService.get(1, 1000, "resources", "", true);
                    const collections = result?.data || [];
                    this.state.collections = this.tools?.sortCollectionsByName(collections) || collections;
                } catch (err) {
                    this.state.collections = [];
                }
                this.state.collectionsLoaded = true;
                this.update();
            },

            onCollectionSelected(value) {
                this.state.selectedCollectionId = value || '';
                this.update();
            },

            handleFilesSelected(data) {
                // Mark a real selection so the close event that close() triggers
                // is not mistaken for a cancel (which would fire onCancel).
                this._filesConfirmed = true;
                const collectionId = this.state.selectedCollectionId || null;
                // Call the callback if provided
                if (this.state.onFilesSelectedCallback) {
                    this.state.onFilesSelectedCallback({ ...data, collectionId });
                }
                // Close the modal
                this.close();
            },

            close() {
                // syncDialogState (see onMounted) notices the dialog is no
                // longer open, resets state and fires the cancel callback when
                // appropriate, so just close here.
                this.$('dialog').close();
            },

            // The user left the page this picker was opened from (browser/mouse
            // back or forward, Alt+Left, or any in-app navigation). Drop the
            // cancel callback first: this is not a cancel, and firing it would
            // pop the host modal (Reupload Content, Upload to Collection) back
            // open on top of whatever page the user just landed on.
            closeForNavigation() {
                const dialog = this.$('dialog');
                if (!dialog || !dialog.open) return;
                if (window.location.hash === this._openedAtHash) return;
                this.state.onCancelCallback = null;
                dialog.close();
            },

            onMounted() {
                // Make globally accessible if needed
                window.filePickerModal = this;

                // Watch the `open` attribute rather than subscribing to the
                // dialog's 'close' event. A Riot re-render of this component
                // can drop `open` while the dialog is showing - that closes it
                // on screen but fires NO 'close' event, so the teardown never
                // ran and a host that had stepped aside (Reupload Content,
                // Upload to Collection) was never restored (METALARK-1209).
                // The attribute moves on every path - close(), Esc, the X, the
                // backdrop, and that re-render - so it is the reliable signal.
                // Observing the root with subtree covers the dialog element
                // being replaced rather than just mutated.
                this._dialogWasOpen = false;
                this._dialogObserver = new MutationObserver(() => this.syncDialogState());
                this._dialogObserver.observe(this.root, {
                    subtree: true,
                    childList: true,
                    attributes: true,
                    attributeFilter: ['open']
                });

                // popstate as well as hashchange: a traversal that pops a
                // pushState entry written by a page for its own URL syncing
                // doesn't always come with a hashchange. Both are guarded on
                // the hash actually having moved, so the pair is idempotent.
                this._closeOnNavigation = () => this.closeForNavigation();
                window.addEventListener('hashchange', this._closeOnNavigation);
                window.addEventListener('popstate', this._closeOnNavigation);
            },

            // Fires onModalClosed once per open->closed transition, whatever
            // closed the dialog. Guarded on _dialogWasOpen so the many
            // mutations an open dialog produces don't re-run the teardown.
            syncDialogState() {
                const dialog = this.$('dialog');
                const isOpen = !!(dialog && dialog.open);
                if (isOpen) {
                    this._dialogWasOpen = true;
                    return;
                }
                if (!this._dialogWasOpen) return;
                this._dialogWasOpen = false;
                this.onModalClosed();
            },

            onBeforeUnmount() {
                if (this._dialogObserver) this._dialogObserver.disconnect();
                window.removeEventListener('hashchange', this._closeOnNavigation);
                window.removeEventListener('popstate', this._closeOnNavigation);
            },

            onModalClosed() {
                // Dismissed without confirming a selection: let a host that
                // stepped aside (e.g. the reupload/collection modal) restore
                // itself. Capture the callback before the reset clears it.
                const onCancel = this._filesConfirmed ? null : this.state.onCancelCallback;
                this._filesConfirmed = false;

                // Unmount file-picker and clean up state when modal is closed
                this.update({
                    showFilePicker: false,
                    repositoryId: null,
                    provider: null,
                    repositoryName: '',
                    onFilesSelectedCallback: null,
                    collections: [],
                    collectionsLoaded: false,
                    selectedCollectionId: '',
                    lockedCollection: false,
                    maxFiles: 0,
                    hideCollection: false,
                    onCancelCallback: null
                });

                if (onCancel) onCancel();
            }
        }
    </script>

    <style>
        .filepicker-help-chevron { display: inline-block; transition: transform 0.2s ease; transform: rotate(90deg); }
        .filepicker-help > input[type="checkbox"]:checked ~ .collapse-title .filepicker-help-chevron { transform: rotate(-90deg); }
    </style>
</file-picker-modal>
